import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  trend?: number;
  trendLabel?: string;
  icon: React.ReactNode;
  variant?: 'default' | 'positive' | 'negative' | 'warning' | 'info';
  size?: 'default' | 'hero';
  className?: string;
}

export default function MetricCard({
  label,
  value,
  subValue,
  trend,
  trendLabel,
  icon,
  variant = 'default',
  size = 'default',
  className = '',
}: MetricCardProps) {
  const variantStyles = {
    default: 'card-elevated',
    positive: 'card-elevated border-positive/20',
    negative: 'card-elevated border-negative/30 bg-negative-subtle',
    warning: 'card-elevated border-warning/20',
    info: 'card-elevated border-info/20',
  };

  const iconBg = {
    default: 'bg-muted text-muted-foreground',
    positive: 'bg-positive-subtle text-positive',
    negative: 'bg-negative-subtle text-negative',
    warning: 'bg-warning-subtle text-warning',
    info: 'bg-info-subtle text-info',
  };

  const trendPositive = trend !== undefined && trend > 0;
  const trendNegative = trend !== undefined && trend < 0;

  return (
    <div className={`${variantStyles[variant]} card-hover p-4 flex flex-col gap-3 ${size === 'hero' ? 'p-5' : ''} ${className}`}>
      <div className="flex items-start justify-between">
        <p className={`text-xs font-semibold uppercase tracking-widest text-muted-foreground ${size === 'hero' ? 'text-sm' : ''}`}>
          {label}
        </p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg[variant]} ${size === 'hero' ? 'w-10 h-10' : ''}`}>
          {icon}
        </div>
      </div>

      <div>
        <p className={`font-bold tabular-nums font-mono text-foreground leading-none ${size === 'hero' ? 'text-3xl' : 'text-2xl'}`}>
          {value}
        </p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
        )}
      </div>

      {trend !== undefined && (
        <div className="flex items-center gap-1.5 mt-auto">
          {trendPositive && <TrendingUp size={12} className="text-positive" />}
          {trendNegative && <TrendingDown size={12} className="text-negative" />}
          {!trendPositive && !trendNegative && <Minus size={12} className="text-muted-foreground" />}
          <span className={`text-xs font-semibold ${trendPositive ? 'text-positive' : trendNegative ? 'text-negative' : 'text-muted-foreground'}`}>
            {trendPositive ? '+' : ''}{trend}%
          </span>
          {trendLabel && (
            <span className="text-xs text-muted-foreground">{trendLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}